import { Plus, Eye, Download, FileText, RefreshCw, Edit, Archive } from 'lucide-react';
import { Button, ButtonGroup } from '@components/base/actions';
import { Icon, Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const sizes = ['sm', 'md', 'lg'] as const;

function ButtonGroupShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="button group">
          <ButtonGroup
            attached={playgroundProps.attached as boolean}
            size={playgroundProps.size as 'sm' | 'md' | 'lg'}
            orientation={playgroundProps.orientation as 'horizontal' | 'vertical'}
          >
            <Button>New Claim</Button>
            <Button variant="secondary">View Details</Button>
            <Button variant="secondary">Export</Button>
          </ButtonGroup>
      </ShowcasePreviewSection>

      {/* Basic Group */}
      <ShowcaseDemoSection
        title="Basic Group"
        description="Related buttons grouped together with a small gap between them"
       
      >
          <ButtonGroup>
            <Button>Submit Claim</Button>
            <Button variant="secondary">Save Draft</Button>
            <Button variant="ghost">Cancel</Button>
          </ButtonGroup>
      </ShowcaseDemoSection>

      {/* Attached Buttons */}
      <ShowcaseDemoSection
        title="Attached Buttons"
        description="Buttons visually connected with no gap, creating a seamless button bar"
       
      >
          <VStack gap={4}>
            <Text size="sm" color="secondary">Policy Actions</Text>
            <ButtonGroup attached>
              <Button variant="secondary">View</Button>
              <Button variant="secondary">Edit</Button>
              <Button variant="secondary">Renew</Button>
            </ButtonGroup>

            <Text size="sm" color="secondary" className="mt-4">Document Actions</Text>
            <ButtonGroup attached>
              <Button>Upload</Button>
              <Button variant="secondary">Download</Button>
              <Button variant="secondary">Print</Button>
              <Button variant="secondary">Share</Button>
            </ButtonGroup>
          </VStack>
      </ShowcaseDemoSection>

      {/* Vertical Orientation */}
      <ShowcaseDemoSection
        title="Vertical Orientation"
        description="Buttons stacked vertically for sidebar or compact layouts"
       
      >
          <HStack gap={8} align="start">
            <VStack gap={2}>
              <Text size="sm" color="secondary">Detached</Text>
              <ButtonGroup orientation="vertical">
                <Button variant="secondary">View Policy</Button>
                <Button variant="secondary">Edit Coverage</Button>
                <Button variant="secondary">Add Rider</Button>
              </ButtonGroup>
            </VStack>

            <VStack gap={2}>
              <Text size="sm" color="secondary">Attached</Text>
              <ButtonGroup orientation="vertical" attached>
                <Button variant="secondary">View Policy</Button>
                <Button variant="secondary">Edit Coverage</Button>
                <Button variant="secondary">Add Rider</Button>
              </ButtonGroup>
            </VStack>
          </HStack>
      </ShowcaseDemoSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Button groups support three sizes that apply to all child buttons"
       
      >
          <VStack gap={4}>
            {sizes.map((size) => (
              <VStack key={size} gap={2}>
                <Text size="sm" color="secondary">{size.toUpperCase()}</Text>
                <ButtonGroup size={size} attached>
                  <Button variant="secondary">Approve</Button>
                  <Button variant="secondary">Reject</Button>
                  <Button variant="secondary">Defer</Button>
                </ButtonGroup>
              </VStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* With Icons */}
      <ShowcaseDemoSection
        title="With Icons"
        description="Button groups work seamlessly with icon buttons"
       
      >
          <VStack gap={4}>
            <Text size="sm" color="secondary">Claim Actions</Text>
            <ButtonGroup attached>
              <Button leftIcon={<Icon icon={Plus} size="sm" />}>New Claim</Button>
              <Button variant="secondary" leftIcon={<Icon icon={Eye} size="sm" />}>View</Button>
              <Button variant="secondary" leftIcon={<Icon icon={Edit} size="sm" />}>Edit</Button>
            </ButtonGroup>

            <Text size="sm" color="secondary" className="mt-4">Document Toolbar</Text>
            <ButtonGroup attached>
              <Button variant="secondary" leftIcon={<Icon icon={FileText} size="sm" />} aria-label="View document" />
              <Button variant="secondary" leftIcon={<Icon icon={Download} size="sm" />} aria-label="Download document" />
              <Button variant="secondary" leftIcon={<Icon icon={RefreshCw} size="sm" />} aria-label="Refresh" />
              <Button variant="secondary" leftIcon={<Icon icon={Archive} size="sm" />} aria-label="Archive" />
            </ButtonGroup>
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const ButtonGroupShowcase: ShowcaseDefinition = {
  id: 'button-group',
  name: 'ButtonGroup',
  category: 'actions',
  description: 'Group of related buttons displayed together',
  propsDefinition: [
    {
      name: 'attached',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether buttons are visually connected with no gap',
    },
    {
      name: 'size',
      type: 'select',
      options: ['sm', 'md', 'lg'],
      defaultValue: 'md',
      description: 'Size of all buttons in group',
    },
    {
      name: 'orientation',
      type: 'select',
      options: ['horizontal', 'vertical'],
      defaultValue: 'horizontal',
      description: 'Layout direction of the button group',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="toolbar"',
      'aria-orientation (horizontal/vertical)',
    ],
    keyboardNav: [
      'Arrow keys: Move between buttons',
      'Tab: Move focus into/out of group',
    ],
    notes: [
      'Groups related actions for visual association',
      'Attached mode creates seamless button bar',
      'Size prop overrides individual button sizes',
    ],
  },
  component: ButtonGroupShowcaseComponent,
};
